<template>
    <div class="mb-[100px]">
        <div class="mb-[27px] mt-[42px] flex h-[42px] justify-between">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                <span class="flex flex-wrap items-center gap-2">
                    <span class="flex items-center gap-2">
                        <span
                            class="h-2 w-2 rounded-full"
                            :class="
                                domainState.active
                                    ? 'bg-green-500'
                                    : 'bg-gray-400'
                            "
                        ></span>
                        <span class="text-gray-800">
                            {{ reportedDomain.domain }}
                        </span>
                    </span>
                </span>
            </h1>
        </div>

        <BasicTabs :tabs="tabs" :tab-key="'domain-dmarc-management'" />

        <div class="mt-[42px] grid grid-cols-[260px_1fr] gap-12">
            <div class="flex flex-col">
                <div
                    :class="{
                        'mb-[500px]': showDnsRecords,
                        'mb-32': !showDnsRecords,
                    }"
                >
                    <h3 class="text-lg font-semibold text-gray-800">
                        {{ $t("vue.domain_status") }}
                    </h3>
                    <p class="text-sm text-gray-500">
                        {{ $t("vue.basic_info_about_your_domain") }}
                    </p>
                </div>

                <div>
                    <h3 class="text-lg font-semibold text-gray-800">
                        {{ $t("vue.dmarc_configuration") }}
                    </h3>
                    <p class="text-sm text-gray-500">
                        {{ $t("vue.update_your_txt_record") }}
                    </p>
                </div>
            </div>

            <div class="space-y-6">
                <div class="rounded-[12.8px] border border-gray-200 bg-white">
                    <div
                        class="grid grid-cols-2 gap-[21px] px-[21px] py-[21px]"
                    >
                        <div>
                            <div
                                class="text-[12px] font-medium leading-[140%] text-gray-700"
                            >
                                {{ $t("vue.status") }}
                            </div>
                            <div class="mt-1">
                                <span
                                    class="policy-badge"
                                    :class="
                                        domainState.active
                                            ? 'badge-active'
                                            : 'badge-inactive'
                                    "
                                >
                                    {{
                                        domainState.active
                                            ? $t("vue.active")
                                            : $t("vue.inactive")
                                    }}
                                </span>
                            </div>
                        </div>

                        <div>
                            <div
                                class="text-[12px] font-medium leading-[140%] text-gray-700"
                            >
                                {{ $t("vue.dmarc_policy") }}
                            </div>
                            <div class="mt-1">
                                <span
                                    class="policy-badge capitalize"
                                    :class="policyBadgeClass"
                                >
                                    {{ policyDisplayLabel }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div v-if="showDnsRecords">
                        <div
                            class="border-t border-gray-200 px-[21px] py-[21px]"
                        >
                            <div class="flex flex-col gap-[13px]">
                                <div>
                                    <div
                                        class="text-[12px] font-semibold leading-[140%] text-gray-800"
                                    >
                                        DMARC
                                    </div>
                                    <p
                                        class="mt-1 text-[10.5px] leading-[142%] text-gray-500"
                                    >
                                        {{
                                            $t(
                                                "vue.dmarc_management_dmarc_description",
                                            )
                                        }}
                                    </p>
                                </div>
                                <div
                                    class="rounded-md border border-gray-200 bg-gray-50 px-[14px] py-[10px] text-[10.5px] leading-[140%] text-gray-700"
                                >
                                    {{ detectedDmarcRecordDisplay }}
                                </div>
                            </div>
                        </div>

                        <div
                            class="border-t border-gray-200 px-[21px] py-[21px]"
                        >
                            <div class="flex flex-col gap-[13px]">
                                <div>
                                    <div
                                        class="text-[12px] font-semibold leading-[140%] text-gray-800"
                                    >
                                        SPF
                                    </div>
                                    <p
                                        class="mt-1 text-[10.5px] leading-[142%] text-gray-500"
                                    >
                                        {{
                                            $t(
                                                "vue.dmarc_management_spf_description",
                                            )
                                        }}
                                    </p>
                                </div>
                                <div
                                    class="rounded-md border border-gray-200 bg-gray-50 px-[14px] py-[10px] text-[10.5px] leading-[140%] text-gray-700"
                                >
                                    {{ spfRecordDisplay }}
                                </div>
                            </div>
                        </div>

                        <div
                            class="border-t border-gray-200 px-[21px] py-[21px]"
                        >
                            <div class="flex flex-col gap-[13px]">
                                <div>
                                    <div
                                        class="text-[12px] font-semibold leading-[140%] text-gray-800"
                                    >
                                        DKIM
                                    </div>
                                    <p
                                        class="mt-1 text-[10.5px] leading-[142%] text-gray-500"
                                    >
                                        {{
                                            $t(
                                                "vue.dmarc_management_dkim_description",
                                            )
                                        }}
                                    </p>
                                </div>
                                <div
                                    class="rounded-md border border-gray-200 bg-gray-50 px-[14px] py-[10px] text-[10.5px] leading-[140%] text-gray-700"
                                >
                                    {{ dkimSelectorsDisplay }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="border-t border-gray-200 px-[21px] py-[10px]">
                        <div class="flex items-center justify-end gap-[14px]">
                            <button
                                class="inline-flex cursor-pointer items-center gap-[3.65px] text-[10.5px] font-medium leading-[140%] text-blue-600 hover:text-blue-700"
                                @click="showDnsRecords = !showDnsRecords"
                            >
                                <span>
                                    {{
                                        showDnsRecords
                                            ? $t("vue.hide_dns_records")
                                            : $t("vue.show_dns_records")
                                    }}
                                </span>
                                <component
                                    :is="
                                        showDnsRecords ? ChevronUp : ChevronDown
                                    "
                                    class="h-[14px] w-[9px]"
                                />
                            </button>

                            <button
                                class="relative inline-flex h-[28px] cursor-pointer items-center justify-center gap-[3.65px] rounded-[6px] bg-blue-500 px-[14px] text-[10.5px] font-medium leading-[140%] text-white transition hover:bg-blue-600"
                                :aria-busy="
                                    dnsCheckingInProgress ? 'true' : 'false'
                                "
                                @click="checkDnsRecords"
                            >
                                <span
                                    v-if="showPulse"
                                    :key="pulseKey"
                                    class="pulse-ring pointer-events-none absolute -inset-1 rounded-[6px]"
                                    aria-hidden="true"
                                    @animationend="showPulse = false"
                                />
                                <RefreshCw class="h-[14px] w-[14px]" />
                                {{ $t("vue.reload_dns_records") }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="rounded-[12.8px] border border-gray-200 bg-white">
                    <div
                        class="flex items-start justify-between gap-6 px-[24px] pt-[18px]"
                    >
                        <div>
                            <h3
                                class="text-[12px] font-medium leading-[140%] text-black"
                            >
                                {{ $t("vue.configure_your_txt_record") }}
                            </h3>
                            <p
                                class="mt-1 text-[10.5px] leading-[142%] text-[#4B5563]"
                            >
                                {{ $t("vue.set_your_dmarc_policy") }}
                            </p>
                        </div>

                        <div class="flex items-center gap-[6px]">
                            <span
                                class="rounded-md bg-gray-100 px-[10px] py-[6px] text-[10.5px] font-medium text-gray-700"
                                >DMARC</span
                            >
                            <span
                                class="rounded-md px-[10px] py-[6px] text-[10.5px] text-gray-400"
                                >SPF</span
                            >
                            <span
                                class="rounded-md px-[10px] py-[6px] text-[10.5px] text-gray-400"
                                >DKIM</span
                            >
                            <span
                                class="rounded-md px-[10px] py-[6px] text-[10.5px] text-gray-400"
                                >BIMI</span
                            >
                        </div>
                    </div>

                    <div class="px-[24px] pb-[24px] pt-[18px]">
                        <DmarcRecordConfigurator
                            v-model="txtRecord"
                            host-name="_dmarc"
                        />

                        <DnsProviderSetBlock
                            :domain-name="reportedDomain.domain"
                            :desired-dmarc-record="txtRecord"
                            :detected-dns-provider="detectedDnsProvider"
                            :show-one-click-block="isCloudflare"
                            user-source-destination="dmarc-management"
                        />

                        <EmailInstructions
                            class="mt-[24px]"
                            :domain="reportedDomain.domain"
                            :txt-record="txtRecord"
                            title-key="vue.email_instructions_to_developer"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import axios from "axios";
import route from "ziggy-js";
import { computed, onMounted, reactive, ref, watch, type PropType } from "vue";
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import { ChevronDown, ChevronUp, RefreshCw, Copy } from "lucide-vue-next";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";
import { buildDomainReportTabs } from "@/Pages/Reports/utils/buildDomainReportTabs";
import EmailInstructions from "@/components/Special/EmailInstructions.vue";

const { t } = useTranslations();

interface ReportedDomain {
    domain: string;
    active: boolean;
    created_at?: string;
    dmarcPolicy: string;
    dmarcRecord?: string | null;
    account: string;
    parked?: boolean;
}

interface DomainInfo {
    dnsProvider?: string | null;
    spfRecord?: string | null;
    dmarcRecord?: string | null;
    dkimSelectors?: string[] | null;
}

const props = defineProps({
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
    domainInfo: {
        type: Object as PropType<DomainInfo>,
        required: false,
        default: () => ({}),
    },
    isCloudflare: {
        type: Boolean,
        required: false,
        default: true,
    },
});

const tabs = computed(() =>
    buildDomainReportTabs(
        props.reportedDomain.domain,
        props.reportedDomain?.parked ?? false,
        t,
    ),
);

const domainState = reactive({
    active: false,
    dmarcPolicy: "none",
    dmarcRecord: null as string | null,
});

const policy = ref(
    props.reportedDomain.dmarcPolicy?.toString() || t("vue.none"),
);
const detectedDmarcRecordRaw = ref<string | null>(null);
const detectedSpfRecordRaw = ref<string | null>(
    props.domainInfo?.spfRecord ?? null,
);
const detectedDkimSelectors = ref<string[]>(
    props.domainInfo?.dkimSelectors ?? [],
);
const detectedDmarcRecordDisplay = ref<string[]>(
    props.domainInfo?.dmarcRecord ?? t("vue.not_detected"),
);

const dnsCheckingInProgress = ref(false);
const showDnsRecords = ref(false);

const pulseKey = ref(0);
const showPulse = ref(false);

onMounted(() => {
    domainState.active = props.reportedDomain.active;
    domainState.dmarcPolicy = props.reportedDomain.dmarcPolicy;
    domainState.dmarcRecord = props.reportedDomain.dmarcRecord ?? null;
});

const generatedTxtRecord = computed(
    () =>
        `v=DMARC1; p=${policy.value}; pct=100; rua=mailto:${props.reportedDomain.account}@rua.dmarceye.com; fo=1; ri=86400`,
);

const baseTxtRecord = computed(() => {
    if (detectedDmarcRecordRaw.value) {
        return applyPolicyToDmarcRecord(
            detectedDmarcRecordRaw.value,
            policy.value,
        );
    }

    const dmarcRecord = props.reportedDomain.dmarcRecord;

    if (dmarcRecord === "" || dmarcRecord === null) {
        return generatedTxtRecord.value;
    }

    return applyPolicyToDmarcRecord(dmarcRecord, policy.value);
});

const txtRecord = ref(baseTxtRecord.value);
const lastBaseTxtRecord = ref(baseTxtRecord.value);

watch(
    () => baseTxtRecord.value,
    (value) => {
        if (txtRecord.value === lastBaseTxtRecord.value) {
            txtRecord.value = value;
        }

        lastBaseTxtRecord.value = value;
    },
    { immediate: true },
);

const spfRecordDisplay = computed(() =>
    normalizeDnsValue(
        detectedSpfRecordRaw.value ?? props.domainInfo?.spfRecord,
    ),
);

const dkimSelectorsDisplay = computed(() => {
    const selectors = detectedDkimSelectors.value.length
        ? detectedDkimSelectors.value
        : (props.domainInfo?.dkimSelectors ?? []);

    if (selectors.length === 0) {
        return t("vue.not_detected");
    }

    return t("vue.dkim_selectors_found", {
        selectors: selectors.join(", "),
    });
});

const detectedDnsProvider = computed(() =>
    (props.domainInfo?.dnsProvider || "").trim(),
);

const policyDisplay = computed(() => {
    const currentPolicy = domainState.dmarcPolicy?.trim() || "";

    if (currentPolicy === "") {
        return t("vue.none");
    }

    return currentPolicy;
});

const policyDisplayLabel = computed(() => {
    const policy = policyDisplay.value.toLowerCase();

    if (policy === "reject") return t("vue.reject");
    if (policy === "quarantine") return t("vue.quarantine");
    if (policy === "none") return t("vue.none");

    return policyDisplay.value;
});

const policyBadgeClass = computed(() => {
    if (policyDisplay.value === "reject") {
        return "policy-reject";
    }

    if (policyDisplay.value === "quarantine") {
        return "policy-quarantine";
    }

    return "policy-none";
});

function triggerPulse() {
    pulseKey.value += 1;
    showPulse.value = true;
}

async function checkDnsRecords() {
    triggerPulse();

    if (dnsCheckingInProgress.value) {
        return;
    }

    dnsCheckingInProgress.value = true;

    try {
        const response = await axios.get(
            route("domains.check-dns", { domain: props.reportedDomain.domain }),
        );

        if (!response.data.success) {
            console.warn("DNS checking failed:", response.data.error);
            return;
        }

        const result = response.data.result;

        domainState.active = result.active;
        domainState.dmarcPolicy = result.dmarc_policy;
        domainState.dmarcRecord = result.dmarc_record ?? null;

        if (result.dmarc_policy) {
            policy.value = result.dmarc_policy;
        }

        detectedDmarcRecordDisplay.value = result.dmarc_record ?? null;
        detectedSpfRecordRaw.value = result.spf_record ?? null;
        detectedDkimSelectors.value = Array.isArray(result.dkim_selectors)
            ? result.dkim_selectors
            : [];

        create({
            title: t("vue.record_checked"),
            type: "success",
        });
    } catch (error) {
        console.error("Failed to check DNS records:", error);
    } finally {
        dnsCheckingInProgress.value = false;
    }
}

async function copyRecord() {
    try {
        if (navigator.clipboard) {
            await navigator.clipboard.writeText(txtRecord.value);
        } else {
            const el = document.createElement("textarea");
            el.value = txtRecord.value;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
        }

        create({
            title: t("vue.copied_to_clipboard"),
            type: "success",
        });
    } catch (error) {
        console.error("Failed to copy DMARC record:", error);
    }
}

function applyPolicyToDmarcRecord(record: string, newPolicy: string) {
    if (!record) {
        return record;
    }

    if (/;\s*p\s*=/i.test(record) || /\bp\s*=/i.test(record)) {
        return record.replace(
            /(\bp\s*=\s*)(none|quarantine|reject)\b/i,
            `$1${newPolicy}`,
        );
    }

    return record.replace(/v\s*=\s*DMARC1\s*;?/i, (match) => {
        const hasSemicolon = match.trim().endsWith(";");
        return hasSemicolon
            ? `${match} p=${newPolicy};`
            : `${match}; p=${newPolicy};`;
    });
}

function normalizeDnsValue(value?: string | null): string {
    if (!value) {
        return t("vue.not_detected");
    }

    const normalized = value.trim();

    if (normalized === "") {
        return t("vue.not_detected");
    }

    return normalized;
}
</script>

<style scoped>
.pulse-ring {
    color: rgb(147 197 253 / 0.85);
    box-shadow: 0 0 0 2px currentColor;
    animation: ringPulse 900ms ease-out both;
    transform-origin: center;
}

@keyframes ringPulse {
    0% {
        transform: scale(0.98);
        opacity: 0;
    }
    15% {
        opacity: 1;
    }
    100% {
        transform: scale(1.08);
        opacity: 0;
    }
}
</style>
